// @flow strict

import React, { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import ReservationItem from "../../components/ReservationItem";
import userManagementStore from "../../store/userManagementStore";
import { FaCalendar, FaCalendarAlt, FaSortAmountDown } from "react-icons/fa";
import { MdClose, MdOutlineFilterAlt } from "react-icons/md";
import { BiSearch } from "react-icons/bi";
import { FiX } from "react-icons/fi";

function RendezVousPage() {
  const {
    isLoadingReservation: isLoading,
    reservations,
    getAllReservations,
    temporaryReservation,
  } = userManagementStore();

  // DÉPLACER TOUS LES STATES AU DÉBUT
  const [filteredReservation, setFilteredReservation] = useState([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchDate, setSearchDate] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [sortOrder, setSortOrder] = useState("recent");
  const [selectedStatuses, setSelectedStatuses] = useState([]); // DÉPLACÉ ICI

  const searchInputRef = useRef(null);
  const dropdownRef = useRef(null);

  const statusList = ["PENDING", "CONFIRMED", "CANCELLED", "COMPLETED"];
  const STATUS_LABELS = {
    PENDING: "En attente",
    CONFIRMED: "Confirmé",
    CANCELLED: "Annulé",
    COMPLETED: "Terminé",
  };

  let visibleStatuses = statusList;

  // Calculer les totaux
  const calculateTotals = (reservations) => {
    const totalServices = reservations.reduce((total, reservation) => {
      return total + (reservation.appointmentServices?.length || 0);
    }, 0);

    const totalAmount = reservations.reduce((total, reservation) => {
      return total + parseFloat(reservation.totalAmount || 0);
    }, 0);

    return { totalServices, totalAmount };
  };

  const { totalServices, totalAmount } = calculateTotals(filteredReservation);

  useEffect(() => {
    getAllReservations();
  }, []);

  // Fonction de filtrage et tri
  const filterAndSortReservations = () => {
    let filtered = reservations || [];

    // Filtre par statut
    if (selectedStatuses.length > 0) {
      filtered = filtered.filter((r) => selectedStatuses.includes(r?.status));
    }

    // Filtre par date
    if (searchDate) {
      filtered = filtered.filter((r) => {
        if (!r?.date) return false;
        const rdvDate = new Date(r.date).toISOString().split("T")[0];
        return rdvDate === searchDate;
      });
    }

    // Filtre par recherche texte
    if (searchTerm.trim() !== "") {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter((r) => {
        const clientName = `${r.client?.prenom || ""} ${
          r.client?.nom || ""
        }`.toLowerCase();
        const clientEmail = r.client?.email?.toLowerCase() || "";
        const clientPhone = r.client?.telephone || "";

        return (
          clientName.includes(searchLower) ||
          clientEmail.includes(searchLower) ||
          clientPhone.includes(searchLower)
        );
      });
    }

    // Tri par date
    filtered.sort((a, b) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);

      if (sortOrder === "recent") {
        return dateB - dateA; // Plus récent en premier
      } else {
        return dateA - dateB; // Plus ancien en premier
      }
    });

    // Filtrer les réservations valides
    const validReservations = filtered.filter(
      (reservation) => reservation && reservation.id,
    );

    setFilteredReservation(validReservations);
  };

  useEffect(() => {
    filterAndSortReservations();
  }, [selectedStatuses, reservations, searchDate, searchTerm, sortOrder]);

  // Gestion de la fermeture du dropdown en cliquant à l'extérieur
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const toggleStatus = (status) => {
    setSelectedStatuses((prev) =>
      prev.includes(status)
        ? prev.filter((s) => s !== status)
        : [...prev, status],
    );
  };

  const removeStatus = (statusToRemove) => {
    setSelectedStatuses((prev) =>
      prev.filter((status) => status !== statusToRemove),
    );
  };

  const clearAllFilters = () => {
    setSelectedStatuses([]);
    setSearchDate("");
    setSearchTerm("");
    setSortOrder("recent");
  };

  const isStatusSelected = (status) => selectedStatuses.includes(status);

  // Fonction pour changer l'ordre de tri
  const toggleSortOrder = () => {
    setSortOrder(sortOrder === "recent" ? "oldest" : "recent");
  };

  if (isLoading) {
    return (
      <motion.div
        key="loading"
        initial={{ opacity: 0, x: -50 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: 50 }}
        transition={{ duration: 0.4 }}
        className="h-screen flex justify-center items-center"
      >
        <div className="flex items-center font-nunito text-[14px] text-text">
          <div className="animate-spin rounded-full h-4 w-4 border-t-4 border-b-4 border-white mr-1" />
          Chargement de vos données...
        </div>
      </motion.div>
    );
  }

  document.title = `Liste des rendez-vous Réservation`;

  // Vérifier si des filtres sont actifs
  const hasActiveFilters =
    selectedStatuses.length > 0 || searchDate || searchTerm;

  return (
    <div className="h-full relative">
      {/* En-tête */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-6 gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="font-poppins font-semibold text-[18px] text-title">
              Rendez-vous
            </h2>
            <span className="bg-gray-100 p-2 font-nunito text-[14px] text-text">
              {filteredReservation.length} /{" "}
              {(reservations && reservations.length) || 0}
            </span>
          </div>

          <div className="flex items-center gap-1">
            <p className="font-nunito text-[14px] text-text">
              Affichez, filtrez et exportez les rendez-vous pris par vos
              clients.
            </p>
          </div>
        </div>

        {/* Indicateurs de totaux */}
        {filteredReservation.length > 0 && (
          <div className="flex gap-4 font-nunito text-[14px]">
            <div className="bg-secondary-50 px-3 py-2">
              <span className="font-poppins font-bold text-[16px] text-secondary-700">
                {totalServices}
              </span>
              <span className="text-secondary-600 ml-1">services</span>
            </div>
            <div className="bg-green-50 px-3 py-2">
              <span className="font-poppins font-bold text-[16px] text-green-700">
                {totalAmount.toFixed(2)}
              </span>
              <span className="text-green-600 ml-1">F CFA</span>
            </div>
          </div>
        )}
      </div>

      <div className="w-full bg-slate-100 mb-3 md:mb-0 md:p-3">
        {/* Container principal */}
        <div className="flex flex-col lg:flex-row gap-4 lg:gap-6">
          {/* Partie gauche : Recherche et Filtres */}
          <div className="flex-1">
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              {/* Barre de recherche - Prend toute la largeur sur mobile */}
              <div className="flex-1 min-w-0">
                <div className="relative group">
                  <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                    <BiSearch
                      size={20}
                      className="text-gray-400 group-focus-within:text-secondary"
                    />
                  </div>

                  <input
                    ref={searchInputRef}
                    type="text"
                    placeholder="Rechercher par nom, email ou téléphone..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-10 py-2.5 font-nunito text-[14px] border border-gray-300 focus:outline-none focus:ring-2 focus:ring-secondary focus:border-transparent transition-all duration-200 bg-white"
                  />

                  {searchTerm.length > 0 && (
                    <button
                      className="absolute inset-y-0 right-0 flex items-center pr-3 hover:bg-gray-100 transition-colors duration-200"
                      aria-label="Effacer la recherche"
                      onClick={() => {
                        setSearchTerm("");
                        searchInputRef.current?.focus();
                      }}
                    >
                      <FiX
                        size={18}
                        className="text-gray-400 hover:text-gray-600"
                      />
                    </button>
                  )}
                </div>
              </div>

              {/* Filtre date et bouton filtres - alignés horizontalement */}
              <div className="relative flex flex-row items-stretch gap-2 sm:gap-3">
                {/* Filtre par date */}
                <div className="relative flex-1 xs:flex-none h-full">
                  <div className="relative group">
                    <input
                      type="date"
                      value={searchDate}
                      onChange={(e) => setSearchDate(e.target.value)}
                      className="w-full sm:w-auto px-3 py-2.5 font-nunito text-[14px] border border-gray-300 focus:ring-2 focus:ring-secondary focus:border-transparent transition-all duration-200 bg-white"
                    />
                    {searchDate && (
                      <button
                        onClick={() => setSearchDate("")}
                        className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors duration-200"
                      >
                        <FiX size={16} />
                      </button>
                    )}
                  </div>
                </div>

                {/* Bouton Filtres avec Dropdown */}
                <div className="flex-1 xs:flex-none" ref={dropdownRef}>
                  <button
                    onClick={() => setDropdownOpen(!dropdownOpen)}
                    className={`h-11 sm:h-full flex items-center justify-center gap-2 w-full sm:w-auto px-4 font-nunito text-[14px] font-semibold text-action transition-all duration-200 border ${
                      selectedStatuses.length > 0
                        ? "bg-secondary-700 text-white border-secondary-700 shadow-sm"
                        : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50 hover:border-gray-400"
                    }`}
                  >
                    <MdOutlineFilterAlt size={17} />
                    <span className="hidden xs:inline">Filtres</span>
                    {selectedStatuses.length > 0 && (
                      <span className="bg-white text-secondary-700 font-nunito text-[14px] w-5 h-5 flex items-center justify-center">
                        {selectedStatuses.length}
                      </span>
                    )}
                  </button>

                  {/* Dropdown Menu */}
                  {dropdownOpen && (
                    <div className="absolute top-full mt-2 right-0 bg-white border border-gray-200 shadow-xl z-50 max-h-80 overflow-y-auto">
                      {/* En-tête */}
                      <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
                        <div className="flex justify-between items-center">
                          <span className="font-poppins font-semibold text-[18px] text-title">
                            Filtrer par statut
                          </span>
                          {selectedStatuses.length > 0 && (
                            <button
                              onClick={clearAllFilters}
                              className="font-nunito text-[14px] font-semibold text-action text-secondary-600 hover:text-secondary-800 transition-colors duration-200"
                            >
                              Tout effacer
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Option Tous */}
                      <button
                        onClick={() => {
                          setSelectedStatuses([]);
                          setDropdownOpen(false);
                        }}
                        className={`flex items-center justify-between w-full px-4 py-3 text-left font-nunito text-[14px] transition-all duration-200 border-b border-gray-100 ${
                          selectedStatuses.length === 0
                            ? "bg-secondary-50 text-secondary-800 font-semibold"
                            : "text-gray-700 hover:bg-gray-50"
                        }`}
                      >
                        <span>Tous les statuts</span>
                        {selectedStatuses.length === 0 && (
                          <div className="w-2 h-2 bg-secondary-600"></div>
                        )}
                      </button>

                      {/* Liste des statuts */}
                      <div className="max-h-48 overflow-y-auto">
                        {visibleStatuses.map((status) => (
                          <button
                            key={status}
                            onClick={() => toggleStatus(status)}
                            className={`flex items-center justify-between w-full px-4 py-2.5 text-left font-nunito text-[14px] transition-all duration-200 hover:bg-gray-50 ${
                              isStatusSelected(status)
                                ? "bg-secondary-50 text-secondary-800 font-medium"
                                : "text-gray-700"
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <div
                                className={`w-3 h-3 border ${
                                  isStatusSelected(status)
                                    ? "bg-secondary-600 border-secondary-600"
                                    : "bg-white border-gray-300"
                                }`}
                              ></div>
                              <span>{STATUS_LABELS[status] || status}</span>
                            </div>
                          </button>
                        ))}
                      </div>

                      {/* Pied du dropdown */}
                      <div className="px-4 py-3 border-t border-gray-100 bg-gray-50">
                        <div className="flex justify-between items-center font-nunito text-[14px]">
                          <span className="text-gray-600">
                            {selectedStatuses.length > 0
                              ? `${selectedStatuses.length} statut(s) sélectionné(s)`
                              : "Aucun filtre appliqué"}
                          </span>
                          <div className="flex gap-2">
                            <button
                              onClick={() => setDropdownOpen(false)}
                              className="px-3 py-1.5 font-nunito text-[14px] font-semibold text-action bg-secondary-600 text-white hover:bg-secondary-700 transition-colors duration-200"
                            >
                              Appliquer
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Partie droite : Tri et actions */}
          <div className="flex flex-col xs:flex-row gap-2 sm:gap-3 justify-between xs:justify-end">
            {/* Bouton de tri */}
            <button
              onClick={toggleSortOrder}
              className="flex items-center justify-center gap-2 px-4 py-2.5 bg-white text-gray-700 font-nunito text-[14px] font-semibold text-action transition-all duration-200 border border-gray-300 hover:bg-gray-50 hover:border-gray-400 min-w-[120px]"
            >
              <FaSortAmountDown size={16} className="flex-shrink-0" />
              <span className="inline truncate">
                {sortOrder === "recent" ? "Plus récents" : "Plus anciens"}
              </span>
            </button>

            {/* Bouton pour effacer tous les filtres */}
            {hasActiveFilters && (
              <button
                onClick={clearAllFilters}
                className="flex items-center justify-center gap-2 px-4 py-2.5 bg-red-50 text-red-700 font-nunito text-[14px] font-semibold text-action transition-all duration-200 border border-red-200 hover:bg-red-100 hover:border-red-300 min-w-[120px]"
              >
                <MdClose size={16} className="flex-shrink-0" />
                <span className="truncate">Effacer tout</span>
              </button>
            )}
          </div>
        </div>

        {/* Indicateur de filtres actifs - pour mobile */}
        {hasActiveFilters && (
          <div className="mt-3 flex flex-wrap gap-2 lg:hidden">
            {searchTerm && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-secondary-100 text-secondary-800 font-nunito text-[14px]">
                Recherche: "{searchTerm}"
                <button
                  onClick={() => setSearchTerm("")}
                  className="hover:text-secondary-900"
                >
                  <FiX size={12} />
                </button>
              </span>
            )}
            {searchDate && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 font-nunito text-[14px]">
                Date: {searchDate}
                <button
                  onClick={() => setSearchDate("")}
                  className="hover:text-green-900"
                >
                  <FiX size={12} />
                </button>
              </span>
            )}
            {selectedStatuses.map((status) => (
              <span
                key={status}
                className="inline-flex items-center gap-1 px-2 py-1 bg-secondary-100 text-secondary-800 font-nunito text-[14px]"
              >
                {STATUS_LABELS[status] || status}
                <button
                  onClick={() => toggleStatus(status)}
                  className="hover:text-secondary-900"
                >
                  <FiX size={12} />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Section des filtres actifs */}
      {hasActiveFilters && (
        <div className="mb-4 p-3 bg-secondary-50">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-poppins font-medium text-[15px] text-subtitle">
              Filtres actifs :
            </span>

            {/* Chips des statuts sélectionnés */}
            {selectedStatuses.map((status) => (
              <div
                key={status}
                className="flex items-center gap-2 px-3 py-1 bg-secondary-700 text-white font-nunito text-[14px] font-semibold text-action"
              >
                <span>{STATUS_LABELS[status] || status}</span>
                <button
                  onClick={() => removeStatus(status)}
                  className="hover:bg-secondary-800 p-1 transition-colors"
                >
                  <MdClose size={14} />
                </button>
              </div>
            ))}

            {/* Chip pour la date */}
            {searchDate && (
              <div className="flex items-center gap-2 px-3 py-1 bg-secondary-600 text-white font-nunito text-[14px] font-semibold text-action">
                <span>Date: {searchDate}</span>
                <button
                  onClick={() => setSearchDate("")}
                  className="hover:bg-secondary-700 p-1 transition-colors"
                >
                  <MdClose size={14} />
                </button>
              </div>
            )}

            {/* Chip pour la recherche */}
            {searchTerm && (
              <div className="flex items-center gap-2 px-3 py-1 bg-green-600 text-white font-nunito text-[14px] font-semibold text-action">
                <span>Recherche: "{searchTerm}"</span>
                <button
                  onClick={() => setSearchTerm("")}
                  className="hover:bg-green-700 p-1 transition-colors"
                >
                  <MdClose size={14} />
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Liste des réservations */}
      <motion.div
        key="showFilteredReservation"
        initial={{ opacity: 0, y: -50 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 50 }}
        transition={{ duration: 0.4 }}
        className="flex-1 min-h-0 md:bg-gray-50 md:border overflow-hidden"
      >
        <div className="h-full overflow-y-auto md:p-6">
          <ul className="w-full space-y-4">
            <AnimatePresence mode="wait">
              {filteredReservation.length === 0 ? (
                <motion.div
                  key="empty"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="text-center text-gray-500 py-8 h-full flex flex-col items-center justify-center"
                >
                  <FaCalendarAlt
                    size={60}
                    className="mx-auto mb-4 text-gray-300"
                  />
                  {hasActiveFilters ? (
                    <p className="font-poppins font-semibold text-[18px] text-gray-400">
                      Aucun rendez-vous trouvé avec ces filtres
                    </p>
                  ) : (
                    <h1 className="font-poppins font-semibold text-[18px] text-gray-400">
                      Aucun rendez-vous pour le moment
                    </h1>
                  )}

                  <p className="font-nunito text-[14px] text-text mt-1">
                    {hasActiveFilters
                      ? "Essayez de modifier vos critères de recherche"
                      : "Ajouter un nouveau rendez-vous"}
                  </p>

                  {hasActiveFilters && (
                    <button
                      onClick={clearAllFilters}
                      className="mt-4 px-4 py-2 font-nunito text-[14px] font-semibold text-action bg-secondary-600 text-white hover:bg-secondary-700 transition-colors"
                    >
                      Effacer tous les filtres
                    </button>
                  )}
                </motion.div>
              ) : (
                filteredReservation.map((reservation) => (
                  <ReservationItem
                    key={reservation.id}
                    item={reservation}
                    withAssign={true}
                    temporaryReservation={temporaryReservation}
                  />
                ))
              )}
            </AnimatePresence>
          </ul>
        </div>
      </motion.div>
    </div>
  );
}

export default RendezVousPage;
